import { type ConnectionItemProps } from "@wayflyer/flyui";
import { IconPage24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import { useFeatureFlagEnabled } from "posthog-js/react";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { CONNECT_APP_ROUTE_PATHS, NAVIGATION_PATHS } from "../../../components/constants";
import type { AccountingDocumentsValidationResult } from "../../../hooks/useAccountingDocumentsValidation";
import type { FormattedFundingApplicationScreen } from "../../../queries/funding-application/queries";
import { getRequiredDocsFromConfiguration } from "../../../hooks/navigation-next/use-funding-application-navigation-next";

import { DOC_TYPE_TO_NAME } from "./constants";

type ConnecitonItemKey = "skipped" | "uploaded" | "failedValidation" | "todo";

type RequiredDocumentType = "profit-and-loss" | "balance-sheet";
const REQUIRED_DOCUMENT_TYPES = ["profit-and-loss", "balance-sheet"] as const;

export const useValidatedDocumentsAsConnectionItems = ({
  fundingApplication,
  fundingApplicationStages,
  validatedDocuments,
  validationResults,
  onDocumentRemove,
}: {
  fundingApplication: FormattedFundingApplicationScreen;
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
  validatedDocuments: FundingApplicationSchemas.ListDocumentsResponse[] | undefined;
  validationResults: Record<RequiredDocumentType, AccountingDocumentsValidationResult>;
  onDocumentRemove: (type: FundingApplicationSchemas.DocumentType, optional: boolean) => void;
}): ConnectionItemProps[] => {
  const intl = useIntl();
  const isNewSetUpFlow = useFeatureFlagEnabled("enable_new_onboarding_setup_flow");
  const { navigate } = useSpaNavigate("FundingApp");
  const areAccountingDocumentsRequired =
    !!fundingApplication.accountingDocumentsRequired ||
    getRequiredDocsFromConfiguration(fundingApplication, fundingApplicationStages).some((docType) =>
      REQUIRED_DOCUMENT_TYPES.includes(docType as RequiredDocumentType)
    );

  const isMarkedAsSkipped = fundingApplication.accountingOptedOut;

  if (!areAccountingDocumentsRequired) {
    return [];
  }

  const getConnectionItemByStatus: Record<
    ConnecitonItemKey,
    (
      docType: RequiredDocumentType,
      matchingDocs?: FundingApplicationSchemas.ListDocumentsResponse
    ) => ConnectionItemProps
  > = {
    skipped: (docType) =>
      ({
        testId: `document-skipped-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage({ id: "funding-application:completed", defaultMessage: "Completed" }),
      } satisfies ConnectionItemProps),

    uploaded: (docType, matchingDocs) =>
      ({
        testId: `document-uploaded-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage(COMMON_MESSAGES.verified),
        subtitle: (
          <ul>
            {matchingDocs?.data.map((doc) => (
              <li key={doc.id}>{doc.name}</li>
            ))}
          </ul>
        ),
      } satisfies ConnectionItemProps),

    failedValidation: (docType, matchingDocs) =>
      ({
        testId: `document-failed-validation-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        badgeStatus: "warning",
        badgeText: intl.formatMessage(COMMON_MESSAGES.needsVerification),
        actions: [
          {
            content: intl.formatMessage(COMMON_MESSAGES.verify),
            onAction: () => navigate(`${CONNECT_APP_ROUTE_PATHS.VERIFY}?document=${docType}`),
          },
        ],
        subtitle: (
          <ul>
            {matchingDocs?.data.map((doc) => (
              <li key={doc.id}>{doc.name}</li>
            ))}
          </ul>
        ),
      } satisfies ConnectionItemProps),

    todo: (docType) =>
      ({
        testId: `document-todo-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        actions: [
          {
            critical: true,
            content: intl.formatMessage({
              id: "funding-application: remove platform label",
              defaultMessage: "Remove",
            }),
            onAction: () => onDocumentRemove(docType, true),
          },
          {
            content: intl.formatMessage(COMMON_MESSAGES.upload),
            onAction: () =>
              isNewSetUpFlow
                ? navigate(`${NAVIGATION_PATHS.ADD_CONNECTION}?platform=accounting_upload`)
                : navigate(`${CONNECT_APP_ROUTE_PATHS.UPLOADS}?document=${docType}`),
          },
        ],
      } satisfies ConnectionItemProps),
  };

  const items: ConnectionItemProps[] = REQUIRED_DOCUMENT_TYPES.map((documentType) => {
    const matchingDocuments = (validatedDocuments ?? []).find((doc) => doc.documentType === documentType);
    let status: ConnecitonItemKey;

    if (isMarkedAsSkipped) {
      status = "skipped";
    } else if (matchingDocuments) {
      if (validationResults[documentType].status === "failed-validation") {
        status = "failedValidation";
      } else {
        status = "uploaded";
      }
    } else {
      status = "todo";
    }

    return getConnectionItemByStatus[status](documentType, matchingDocuments);
  });

  return items;
};
